/**
 * ListDetailPage — shows items in a specific list with grid/table views.
 * Supports adding items from collection or as freeform (unlinked) entries.
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, DetailModal, formatDate, Pill } from '@code-burrow/design-system';
import { UnifiedCollectionView, SearchOverlay } from '@code-burrow/tracker-core';
import BookDetailModal from './BookDetailModal.jsx';

// ── List Item Detail Modal ──────────────────────────────────────────────────

function ListItemDetailModal({ item, onClose, onRemove, onAddToCollection }) {
    if (!item) return null;

    const book = item._book;

    // Build header matching BookDetailModal structure
    const authorStr = book?.author || item.author || null;
    const pageStr = book?.pageCount ? `${book.pageCount} pages` : null;
    const metaParts = [pageStr].filter(Boolean);

    return (
        <DetailModal
            item={item}
            onClose={onClose}
            fields={[]}
            editable={false}
            header={{
                imageUrl: item._imageURL,
                imageAlt: item.title,
                title: item.title,
                subtitle: book
                    ? [
                        authorStr,
                        book.publishedDate && String(book.publishedDate),
                    ].filter(Boolean).join(' · ')
                    : authorStr || undefined,
                tags: book
                    ? (book.subjects?.slice(0, 4) || [])
                    : [],
                extra: (
                    <div>
                        {metaParts.length > 0 && (
                            <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-text-secondary">
                                {metaParts.map((p, i) => <span key={i}>{p}</span>)}
                            </div>
                        )}
                        {book?.goodreadsURL && (
                            <a href={book.goodreadsURL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-2 text-xs text-accent-primary hover:underline">
                                View on Goodreads
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                </svg>
                            </a>
                        )}
                    </div>
                ),
            }}
            extraContent={
                <div className="px-4 pb-4 space-y-2">
                    {/* Collection status */}
                    {item._linked ? (
                        <div className="flex items-center gap-2 text-xs text-text-tertiary mb-2">
                            <svg className="h-3.5 w-3.5 text-accent-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                            </svg>
                            In your collection
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 text-xs text-yellow-500 mb-2">
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                                </svg>
                                Not in collection
                            </div>
                            <button
                                onClick={() => onAddToCollection(item)}
                                className="w-full flex items-center justify-center gap-2 py-2.5 text-sm rounded-md bg-accent-primary text-text-inverse hover:bg-accent-primary-hover transition-colors"
                            >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                </svg>
                                Add to Collection
                            </button>
                        </>
                    )}
                    <button
                        onClick={() => onRemove(item._listItemId)}
                        className="w-full flex items-center justify-center gap-2 py-2.5 text-sm rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors"
                    >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        Remove from List
                    </button>
                </div>
            }
        />
    );
}

export default function ListDetailPage({ backendService, pocketbaseURL }) {
    const { listId } = useParams();
    const navigate = useNavigate();
    const [list, setList] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        (async () => {
            try {
                const record = await backendService.db.pb.collection('lists').getOne(listId, { $autoCancel: false });
                setList(record);
            } catch (err) {
                console.error('ListDetailPage fetch error:', err);
                navigate('/lists');
            }
        })();
    }, [listId, backendService, navigate]);

    const handleRefresh = useCallback(() => {
        setRefreshKey((k) => k + 1);
    }, []);

    const handleRemove = useCallback(async (listItemId) => {
        await backendService.deleteListItem(listItemId);
        setSelectedItem(null);
        handleRefresh();
    }, [backendService, handleRefresh]);

    const handleAddToCollection = useCallback((item) => {
        setSelectedItem(null);
        // Open the add overlay with the item's title pre-searched
        setAddOpen(true);
    }, []);

    // Fetch all books for the SearchOverlay's local items
    const [allBooks, setAllBooks] = useState([]);
    useEffect(() => {
        (async () => {
            const result = await backendService.getAllBooks();
            if (result.success) setAllBooks(result.books);
        })();
    }, [backendService, refreshKey]);

    // Add a collection item to this list
    const handleAddFromCollection = useCallback(async (item) => {
        const pbImage = item.coverImage;
        const imageURL = pbImage && pocketbaseURL
            ? `${pocketbaseURL}/api/files/books/${item.id}/${pbImage}`
            : item.coverURL ?? '';
        await backendService.addListItem(listId, {
            itemId: item.id,
            title: item.title,
            imageURL,
        });
        handleRefresh();
    }, [backendService, pocketbaseURL, listId, handleRefresh]);

    // External Google Books search for adding new items to the list
    const handleExternalSearch = useCallback(async (query) => {
        try {
            const result = await backendService.searchBooks(query);
            if (result.success) {
                return { results: result.items.slice(0, 20) };
            }
            return { results: [], error: result.error };
        } catch (err) {
            return { results: [], error: err.message };
        }
    }, [backendService]);

    // Add a Google Books result to the list (fetch details, create if needed, then add as list item)
    const handleExternalAction = useCallback(async (item) => {
        const detailResult = await backendService.getBookDetails(item.googleBooksId);
        if (!detailResult.success) throw new Error(detailResult.error);

        const details = detailResult.details;
        const addResult = await backendService.addBook({
            googleBooksId: details.googleBooksId || item.googleBooksId,
            title: details.title || item.title,
            author: details.author || item.author || 'Unknown',
            description: details.description || '',
            publishedDate: details.publishDate || item.publishedDate || '',
            pageCount: details.pageCount || item.pageCount || null,
            isbn: details.isbn || item.isbn || '',
            coverURL: details.coverURL || item.coverURL || '',
            coverDataURL: details.coverDataURL || '',
            goodreadsURL: details.goodreadsURL || '',
            subjects: details.subjects || item.subjects || [],
        });
        if (!addResult.success) throw new Error(addResult.error);

        // Add to the list
        await backendService.addListItem(listId, {
            itemId: addResult.book.id,
            title: addResult.book.title,
            imageURL: addResult.book.coverURL || details.coverURL || item.coverURL || '',
        });
        handleRefresh();
    }, [backendService, listId, handleRefresh]);

    if (!list) return null;

    const adapter = listCollectionAdapter(backendService, pocketbaseURL, listId, refreshKey, handleRemove, setSelectedItem);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/lists')} className="p-1 text-text-tertiary hover:text-text-primary transition-colors">
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                        </svg>
                    </button>
                    <h1 className="text-3xl font-bold font-display text-text-primary">
                        {list.emoji && <span className="mr-2">{list.emoji}</span>}
                        {list.name}
                    </h1>
                </div>
                <button
                    onClick={() => setAddOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-accent-primary text-text-inverse hover:bg-accent-primary-hover transition-colors"
                >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Add Item
                </button>
            </div>

            <UnifiedCollectionView
                collectionAdapter={adapter}
                refreshKey={`${refreshKey}-${listId}`}
            />

            <SearchOverlay
                open={addOpen}
                onClose={() => setAddOpen(false)}
                items={allBooks}
                onSelect={handleAddFromCollection}
                placeholder="Search books..."
                onExternalSearch={handleExternalSearch}
                onExternalAction={handleExternalAction}
                externalSearchLabel="Search Google Books"
                externalItemKey={(item) => item.googleBooksId}
                renderResult={(item) => {
                    const pbImage = item.coverImage;
                    const imgUrl = pbImage && pocketbaseURL
                        ? `${pocketbaseURL}/api/files/books/${item.id}/${pbImage}`
                        : item.coverURL ?? null;
                    return (
                        <>
                            {imgUrl ? (
                                <img src={imgUrl} alt={item.title} className="w-8 h-12 object-cover rounded flex-shrink-0" />
                            ) : (
                                <div className="w-8 h-12 bg-bg-tertiary rounded flex-shrink-0 flex items-center justify-center">
                                    <svg className="w-4 h-4 text-text-tertiary" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
                                    </svg>
                                </div>
                            )}
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-text-primary truncate font-body">{item.title}</p>
                                {item.author && (
                                    <p className="text-xs text-text-secondary font-body">{item.author}</p>
                                )}
                            </div>
                        </>
                    );
                }}
                renderExternalResult={(item, { onAction, isLoading, isDone }) => (
                    <>
                        {item.coverURL ? (
                            <img src={item.coverURL} alt={item.title} className="w-8 h-12 object-cover rounded flex-shrink-0" />
                        ) : (
                            <div className="w-8 h-12 bg-bg-tertiary rounded flex-shrink-0 flex items-center justify-center">
                                <svg className="w-4 h-4 text-text-tertiary" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
                                </svg>
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-text-primary truncate font-body">{item.title}</p>
                            {item.author && (
                                <p className="text-xs text-text-secondary font-body">{item.author}</p>
                            )}
                            {item.publishedDate && (
                                <p className="text-xs text-text-tertiary font-body">{item.publishedDate}</p>
                            )}
                        </div>
                        <div className="flex-shrink-0">
                            {isDone ? (
                                <span className="text-xs text-green-500 font-medium">Added ✓</span>
                            ) : isLoading ? (
                                <div className="w-4 h-4 border-2 border-text-tertiary border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onAction(); }}
                                    className="p-1 text-text-tertiary hover:text-accent-primary transition-colors"
                                >
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </>
                )}
            />

            <ListItemDetailModal
                item={selectedItem}
                onClose={() => setSelectedItem(null)}
                onRemove={handleRemove}
                onAddToCollection={handleAddToCollection}
            />
        </div>
    );
}

/**
 * Adapter for rendering list items inside UnifiedCollectionView.
 */
function listCollectionAdapter(backendService, pocketbaseURL, listId, _refreshKey, onRemove, onSelect) {
    return {
        storageKey: `book-list-${listId}`,
        defaultSortField: 'position',
        defaultSortDirection: 'asc',

        getImageUrl: (item) => item._imageURL || item.imageURL || null,

        fetchItems: async () => {
            const result = await backendService.getListItems(listId);
            const listItems = result.items || [];

            // Enrich linked items with book data
            let allBooks = [];
            const linkedIds = listItems.filter((li) => li.itemId).map((li) => li.itemId);
            if (linkedIds.length > 0) {
                const bookResult = await backendService.getAllBooks();
                allBooks = bookResult.success ? bookResult.books : [];
            }

            const bookMap = Object.fromEntries(allBooks.map((b) => [b.id, b]));

            return listItems.map((li) => {
                const linked = li.itemId ? bookMap[li.itemId] : null;
                const pbImage = linked?.coverImage;
                const resolvedImage = linked && pbImage && pocketbaseURL
                    ? `${pocketbaseURL}/api/files/books/${linked.id}/${pbImage}`
                    : li.imageURL || null;

                return {
                    ...li,
                    // Merge book data for linked items
                    title: linked?.title || li.title,
                    author: linked?.author || null,
                    publishedDate: linked?.publishedDate || null,
                    _imageURL: resolvedImage,
                    _linked: !!linked,
                    _book: linked || null,
                    // Keep list item fields
                    _listItemId: li.id,
                    notes: li.notes,
                    position: li.position,
                };
            });
        },

        filterFields: [],

        sortFields: [
            { key: 'position', label: 'Position', getValue: (item) => item.position ?? 0 },
            { key: 'title', label: 'Title', getValue: (item) => (item.title ?? '').toLowerCase() },
        ],

        renderGridItem: (item) => {
            return (
                <Card
                    key={item._listItemId || item.id}
                    title={item.title}
                    subtitle={item.author}
                    onClick={() => onSelect(item)}
                    statusPill={
                        !item._linked ? (
                            <div className="mt-1">
                                <Pill className="bg-yellow-500/15 text-yellow-500">Not in collection</Pill>
                            </div>
                        ) : null
                    }
                >
                    {/* Custom image container with portrait aspect ratio for book covers */}
                    <div className="relative w-full aspect-[2/3] bg-bg-tertiary rounded-t-lg overflow-hidden">
                        {item._imageURL ? (
                            <img
                                src={item._imageURL}
                                alt={item.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <svg className="w-16 h-16 text-text-tertiary" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
                                </svg>
                            </div>
                        )}
                    </div>
                    <Card.Content>
                        <Card.Title title={item.title} />
                        <Card.Subtitle subtitle={item.author} />
                        {!item._linked && (
                            <div className="mt-1">
                                <Pill className="bg-yellow-500/15 text-yellow-500">Not in collection</Pill>
                            </div>
                        )}
                    </Card.Content>
                </Card>
            );
        },

        renderTableColumns: [
            {
                key: 'title',
                header: 'Title',
                sortable: 'title',
                renderCell: (item) => (
                    <div className="flex items-center gap-2">
                        <div className="flex flex-col">
                            <span className="font-medium text-text-primary">{item.title}</span>
                            {item.author && <span className="text-xs text-text-tertiary">{item.author}</span>}
                        </div>
                        {!item._linked && (
                            <Pill className="bg-yellow-500/15 text-yellow-500">Not in collection</Pill>
                        )}
                    </div>
                ),
            },
            {
                key: 'notes',
                header: 'Notes',
                renderCell: (item) => (
                    <span className="text-text-secondary text-xs">{item.notes || '-'}</span>
                ),
            },
            {
                key: 'position',
                header: '#',
                sortable: 'position',
                width: '60px',
                renderCell: (item) => (
                    <span className="text-text-tertiary text-xs">{(item.position ?? 0) + 1}</span>
                ),
            },
            {
                key: 'remove',
                header: '',
                width: '40px',
                renderCell: (item) => (
                    <button
                        onClick={(e) => { e.stopPropagation(); onRemove(item._listItemId); }}
                        className="p-1 rounded text-text-tertiary hover:text-red-400 transition-colors"
                        title="Remove from list"
                    >
                        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                ),
            },
        ],

        emptyState: {
            title: 'This list is empty',
            description: 'Add items from your collection or create new entries',
        },

        actions: [],
    };
}
